import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useCartPricingRefreshing } from '../contexts/CartPricingContext';
import { useLocale } from '../contexts/LocaleContext';
import { priceCartForPreview } from '../lib/pricing';
import * as types from '../types';
import useHardwareFirstCheckout from './useHardwareFirstCheckout';

const UPSELL_PREVIEW_CART_ITEM_ID = 'hardware-first-upsell-preview';

/**
 * Server-resolved price for adding one more unit of each sku to the store cart, keyed by sku. Prices
 * every candidate in one call alongside the cart's existing items, so each tile quotes the
 * additional-tracker rate rather than the first-unit rate `useHardwareFirstPdpPricing` returns. A
 * sku is missing while loading, when not enrolled, or when the pricing call fails.
 */
export default function useHardwareFirstUpsellsPricing(
  skus: (string | undefined)[],
): Record<string, types.HardwareDiscountPreview | undefined> {
  const enrolled = useHardwareFirstCheckout();
  const { currency } = useLocale();
  const storeCart = useSelector((state: types.AppState) => state.storeShop.cart);
  // previews with the cart's coupon queue behind each other, so the tiles wait for the cart's own total to land
  const cartPricingRefreshing = useCartPricingRefreshing();
  const [discounts, setDiscounts] = useState<Record<string, types.HardwareDiscountPreview | undefined>>({});
  const skusKey = skus.filter((sku): sku is string => !!sku).join(',');

  useEffect(() => {
    const pricedSkus = skusKey ? skusKey.split(',') : [];
    if (!enrolled || pricedSkus.length === 0) {
      setDiscounts({});
      return undefined;
    }

    if (cartPricingRefreshing) {
      return undefined;
    }

    let cancelled = false;
    // hardware-first prices are keyed by cart item id, so each tile needs its own
    const tileItemId = (sku: string) => `${UPSELL_PREVIEW_CART_ITEM_ID}-${sku}`;
    const cart: types.StoreCart = {
      ...storeCart,
      cartItems: {
        ...storeCart.cartItems,
        ...Object.fromEntries(
          pricedSkus.map((sku) => [
            tileItemId(sku),
            { cartItemId: tileItemId(sku), lineItem: { lineItemId: tileItemId(sku), sku }, quantity: 1 },
          ]),
        ),
      },
      currency,
    };

    priceCartForPreview(cart)
      .then((pricing) => {
        if (!cancelled) {
          setDiscounts(
            Object.fromEntries(pricedSkus.map((sku) => [sku, pricing.hardwareDiscounts?.[tileItemId(sku)]])),
          );
        }
      })
      // already reported inside priceCartForPreview; the tiles then omit their price
      .catch(() => {
        if (!cancelled) {
          setDiscounts({});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enrolled, skusKey, currency, storeCart, cartPricingRefreshing]);

  return discounts;
}
